import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { desc, eq } from "drizzle-orm";
import { ArrowLeft, ScrollText } from "lucide-react";
import { getDb } from "@/db";
import { auditLog, events } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Server functions ────────────────────────────────────────────────

const getAuditLog = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const entries = await db
			.select()
			.from(auditLog)
			.where(eq(auditLog.eventId, event.id))
			.orderBy(desc(auditLog.createdAt))
			.limit(100);

		return { event, entries };
	});

// ── Helpers ─────────────────────────────────────────────────────────

function formatRelativeTime(date: Date | string): string {
	const now = Date.now();
	const then = new Date(date).getTime();
	const diffMs = now - then;
	const diffSec = Math.floor(diffMs / 1000);
	const diffMin = Math.floor(diffSec / 60);
	const diffHr = Math.floor(diffMin / 60);
	const diffDay = Math.floor(diffHr / 24);

	if (diffSec < 60) return "just now";
	if (diffMin < 60) return `${diffMin}m ago`;
	if (diffHr < 24) return `${diffHr}h ago`;
	if (diffDay < 7) return `${diffDay}d ago`;
	return new Date(date).toLocaleDateString();
}

function parseDetails(details: string): Record<string, unknown> {
	try {
		return JSON.parse(details);
	} catch {
		return {};
	}
}

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/events/$eventId/audit")({
	loader: ({ params }) => getAuditLog({ data: params.eventId }),
	component: AuditPage,
});

function AuditPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();

	const { data } = useQuery({
		queryKey: ["audit-log", params.eventId],
		queryFn: () => getAuditLog({ data: params.eventId }),
		initialData,
	});

	const { event, entries } = data;

	return (
		<div className="mx-auto max-w-3xl space-y-6 p-6">
			<Link
				to="/admin/events/$eventId"
				params={{ eventId: params.eventId }}
				className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to event
			</Link>

			<div>
				<h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
				<p className="text-sm text-muted-foreground">{event.name}</p>
			</div>

			{entries.length === 0 ? (
				<div className="flex flex-col items-center gap-3 rounded-xl bg-card p-12 text-center">
					<ScrollText className="h-10 w-10 text-muted-foreground" />
					<p className="text-muted-foreground">No audit log entries yet.</p>
				</div>
			) : (
				<div
					className="space-y-2 overflow-y-auto"
					style={{ maxHeight: "70vh" }}
				>
					{entries.map((entry) => {
						const details = parseDetails(entry.details);
						const detailKeys = Object.keys(details);

						return (
							<div
								key={entry.id}
								className="rounded-xl border border-border bg-card p-4"
							>
								<div className="flex items-start justify-between gap-4">
									<div className="min-w-0 flex-1">
										<p className="font-bold text-foreground">{entry.action}</p>
										{detailKeys.length > 0 && (
											<dl className="mt-1 space-y-0.5 text-sm text-muted-foreground">
												{detailKeys.map((key) => (
													<div key={key} className="flex gap-2">
														<dt className="shrink-0 font-medium">{key}:</dt>
														<dd className="truncate">{String(details[key])}</dd>
													</div>
												))}
											</dl>
										)}
									</div>
									<div className="shrink-0 text-right text-xs text-muted-foreground">
										<p>{formatRelativeTime(entry.createdAt)}</p>
										{entry.userId && (
											<p className="mt-0.5 font-mono">
												{entry.userId.slice(0, 8)}...
											</p>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
